// Cola en la TV del local — pantalla pública por token (taller.tv_cola, 955),
// pensada para una TV 16:9 en el mostrador. Pantalla completa, sin scroll,
// letras grandes. Por privacidad (LOPDP) nunca muestra el nombre del cliente:
// solo el código de orden y el equipo.
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock, Gamepad2, PartyPopper, Sparkles, Wrench } from 'lucide-react';
import { getErrorMessage } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { tallerRpc, ESTADOS, type EstadoOrden } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface TvOrden {
  codigo: string; estado: EstadoOrden; equipo: string; tipo: string;
  prioridad: 'normal' | 'urgente'; fecha_prometida: string | null; desde: string;
}
interface TvColaResp { taller: string; ahora: string; ordenes: TvOrden[]; entregados_hoy: number }

const GRUPOS: { titulo: string; estados: EstadoOrden[] }[] = [
  { titulo: 'Recibido / Diagnóstico', estados: ['recibido', 'diagnostico'] },
  { titulo: 'Presupuesto', estados: ['presupuestado', 'rechazado'] },
  { titulo: 'En cola', estados: ['aprobado'] },
  { titulo: 'En reparación', estados: ['reparacion', 'esperando_repuesto'] },
  { titulo: 'Pruebas', estados: ['pruebas'] },
];
const MAX_LISTOS = 6;
const MAX_POR_GRUPO = 4;
const ROTACION_MS = 10000;

/** Contador que avanza una «página» cada `intervaloMs» (un solo reloj compartido por todos los bloques). */
function useTick(intervaloMs: number): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervaloMs);
    return () => clearInterval(id);
  }, [intervaloMs]);
  return tick;
}
/** Bloque rotativo de un arreglo: según el `tick` compartido, muestra una página de `tamano` elementos. */
function paginar<T>(items: T[], tamano: number, tick: number): T[] {
  const totalPaginas = Math.max(1, Math.ceil(items.length / tamano));
  const p = tick % totalPaginas;
  return items.slice(p * tamano, p * tamano + tamano);
}

function RelojEnVivo() {
  const [ahora, setAhora] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setAhora(new Date()), 1000); return () => clearInterval(id); }, []);
  return (
    <span className="font-mono text-3xl font-black tabular-nums text-white">
      {ahora.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
    </span>
  );
}

function TarjetaListo({ o }: { o: TvOrden }) {
  return (
    <div className="animate-[pulse_2.5s_ease-in-out_infinite] rounded-2xl border-2 border-emerald-400 bg-emerald-500/15 px-5 py-4 shadow-[0_0_30px_rgba(16,185,129,0.35)]">
      <p className="font-mono text-3xl font-black tracking-wider text-emerald-300">{o.codigo}</p>
      <p className="mt-1 flex items-center gap-2 text-lg font-semibold text-white/90"><Gamepad2 className="h-5 w-5 text-emerald-300" /> {o.equipo || '—'}</p>
    </div>
  );
}

function TarjetaEnCurso({ o }: { o: TvOrden }) {
  const paso = ESTADOS[o.estado]?.paso ?? 1;
  const pct = Math.round((paso / 8) * 100);
  return (
    <div className={cn('rounded-xl border px-4 py-3', o.prioridad === 'urgente' ? 'border-amber-400/50 bg-amber-500/10' : 'border-white/10 bg-white/[0.04]')}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono text-xl font-bold text-white">{o.codigo}</p>
        {o.prioridad === 'urgente' && <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-300">Urgente</span>}
      </div>
      <p className="mt-1 truncate text-sm text-white/70">{o.equipo || '—'}</p>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div className="h-full rounded-full bg-cyan-400" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function TvTaller() {
  const { token = '' } = useParams();

  const { data, isLoading, isError, error } = useQuery<TvColaResp>({
    queryKey: ['taller-tv-cola', token],
    queryFn: () => tallerRpc<TvColaResp>('tv_cola', { _token: token }),
    refetchInterval: 20000,
    staleTime: 0,
    retry: 1,
    enabled: !!token,
  });

  const tick = useTick(ROTACION_MS);
  const listos = useMemo(() => (data?.ordenes ?? []).filter((o) => o.estado === 'listo'), [data]);
  const resto = useMemo(() => (data?.ordenes ?? []).filter((o) => o.estado !== 'listo'), [data]);
  const listosPag = paginar(listos, MAX_LISTOS, tick);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-950 text-white">
        <p className="text-2xl font-semibold text-white/60">Cargando…</p>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-slate-950 p-6 text-center text-white">
        <AlertTriangle className="h-14 w-14 text-amber-400" />
        <p className="text-2xl font-bold">Pantalla no válida</p>
        <p className="max-w-md text-white/60">{getErrorMessage(error) || 'El enlace de esta TV venció o no corresponde a ningún taller.'}</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 p-6 text-white">
      {/* Cabecera */}
      <header className="mb-4 flex shrink-0 items-center justify-between">
        <div className="flex items-center gap-3">
          <Wrench className="h-9 w-9 text-fuchsia-400" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-fuchsia-300">Estado de reparaciones</p>
            <h1 className="text-3xl font-black">{data.taller}</h1>
          </div>
        </div>
        <RelojEnVivo />
      </header>

      {/* Listos para retirar */}
      <section className="mb-5 shrink-0">
        <h2 className="mb-2 flex items-center gap-2 text-xl font-extrabold uppercase tracking-wide text-emerald-300">
          <Sparkles className="h-6 w-6" /> Listos para retirar
        </h2>
        {listos.length === 0 ? (
          <p className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4 text-center text-lg text-white/40">Por ahora no hay equipos listos.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {listosPag.map((o) => <TarjetaListo key={o.codigo} o={o} />)}
          </div>
        )}
      </section>

      {/* Etapas */}
      <section className="flex min-h-0 flex-1 gap-3">
        {GRUPOS.map((g) => {
          const ordenesGrupo = resto.filter((o) => g.estados.includes(o.estado));
          const pag = paginar(ordenesGrupo, MAX_POR_GRUPO, tick);
          return (
            <div key={g.titulo} className="flex min-h-0 flex-1 flex-col rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <h3 className="mb-2 shrink-0 text-center text-sm font-bold uppercase tracking-wide text-white/60">{g.titulo} ({ordenesGrupo.length})</h3>
              <div className="flex-1 space-y-2 overflow-hidden">
                {pag.length === 0
                  ? <p className="pt-4 text-center text-xs text-white/30">—</p>
                  : pag.map((o) => <TarjetaEnCurso key={o.codigo} o={o} />)}
              </div>
            </div>
          );
        })}
      </section>

      {/* Pie */}
      <footer className="mt-4 flex shrink-0 items-center justify-between border-t border-white/10 pt-3 text-sm">
        <p className="flex items-center gap-2 font-semibold text-white/80"><PartyPopper className="h-4 w-4 text-emerald-300" /> Entregados hoy: {data.entregados_hoy}</p>
        <p className="flex items-center gap-1.5 text-white/40"><Clock className="h-3.5 w-3.5" /> Por privacidad solo mostramos el número de orden</p>
      </footer>
    </div>
  );
}
